import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { timer } from 'rxjs';
import { FlashcardService } from 'src/app/core/flashcard.service';
import { HeaderService } from 'src/app/core/header.service';
import { LearnsetService } from 'src/app/core/learnset.service';
import { OnboardingService } from 'src/app/core/onboarding.service';
import { PremiumFeatureDialogService } from 'src/app/core/premium-feature-dialog.service';
import { UserService } from 'src/app/core/user.service';
import { Flashcard } from 'src/app/models/flashcard.model';
import { Learnset } from 'src/app/models/learnset.model';
import { Onboarding } from 'src/app/models/onboarding.model';
import { User } from 'src/app/models/user.model';

@Component({
  selector: 'app-learnset-view',
  templateUrl: './learnset-view.component.html',
  styleUrls: ['./learnset-view.component.scss'],
  standalone: false
})
export class LearnsetViewComponent {
  
  user?: User;

  learnset?: Learnset;
  editLearnsetMode: boolean = false;

  currentFlashcard?: Flashcard;
  showFlashcardActions: boolean = false;
  flashcardActions: any = [
    {
      icon: 'pencil',
      text: 'Lernkarte bearbeiten',
      action: 'edit',
      style: 'neutral'
    },
    {
      icon: 'trash',
      text: 'Lernkarte löschen',
      action: 'delete',
      style: 'error'
    }
  ];

  showActions: boolean = false;
  actionsList: any = [
    {
      icon: 'pencil',
      text: 'Lernset umbenennen',
      action: 'rename',
      style: 'neutral'
    },
    {
      icon: 'trash',
      text: 'Lernset löschen',
      action: 'delete',
      style: 'error'
    }
  ];

  showCreateOptions: boolean = false;
  createFlashcardsOptions: any = [
    {
      icon: 'user',
      text: 'Selbst erstellen',
      action: 'manual',
      style: 'neutral'
    },
    {
      icon: 'sparkles',
      text: 'Mit KI erstellen',
      action: 'ai',
      style: 'neutral'
    },
  ];

  showLearnOptions: boolean = false;
  learnOptionsList: any = [
    {
      icon: 'cards',
      text: 'Klassischer Modus',
      action: 'classic-mode',
      style: 'neutral'
    },
    {
      icon: 'forms',
      text: 'Texteingabe-Modus',
      action: 'text-answer-mode',
      style: 'neutral'
    },
    {
      icon: 'microphone',
      text: 'Audio-Modus',
      action: 'audio-mode',
      style: 'neutral'
    }
  ];

  actionsOptions: { icon: string, text: string, action: string, color?: string }[] = [
    { icon: 'pencil', text: 'Lernset umbenennen', action: 'rename' },
    { icon: 'repeat-off', text: 'Smartes Wiederholen deaktivieren', action: 'toggle-smart-repetition' },
    // { icon: 'trash', text: 'Lernset löschen', action: 'delete', color: 'warn' },
  ];

  showSuggestionCards: boolean = false;
  showEditFlashcard: boolean = false;
  showLearnPromptDialog: boolean = false;

  showManualCreate: boolean = false;
  breadcrumbs: any;

  suggestionFlashcards: Flashcard[] = [];

  onboarding?: Onboarding | null;

  smartLearningSelected: boolean = true;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private learnsetService: LearnsetService,
    private flashcardService: FlashcardService,
    private headerService: HeaderService,
    private userService: UserService,
    private premiumFeatureDialogService: PremiumFeatureDialogService,
    private onboardingService: OnboardingService
  ) {}

  ngOnInit(): void {
    const learnsetId = this.route.snapshot.paramMap.get('id');
    this.fetchLearnset(learnsetId!);

    this.userService.getUser().subscribe((user) => this.user = user);

    this.headerService.getActionsClickObservable().subscribe(() => {
      this.showActions = true;
    });

    this.learnsetService.getSuggestionFlashcards(learnsetId!).subscribe((flashcards: any) => {
      this.suggestionFlashcards = flashcards.map((flashcard: any) => Flashcard.constructFlashcard(flashcard));
    });

    this.onboardingService.onboarding$.subscribe((onboarding: Onboarding | null) => {
      this.onboarding = onboarding;
    });

    this.route.queryParamMap.subscribe(params => {
      const newCardsAdded = params.get('newCardsAdded') === 'true';
      if (newCardsAdded && this.onboarding?.learnPromptShown === false) {
        timer(2000).subscribe(() => { // 2 Sekunden Verzögerung
          this.showLearnPromptDialog = true;
        });
      }
    });
  }

  handleAction(action: string) {
    this.showActions = false;
    if (action === 'rename') {
      this.editLearnsetMode = true;
    } if (action === 'toggle-smart-repetition') {
      this.learnset!.smartRepetitionEnabled = !this.learnset!.smartRepetitionEnabled;
      const actionOption = this.actionsOptions.find(option => option.action === 'toggle-smart-repetition');
      actionOption!.text = this.learnset!.smartRepetitionEnabled ? 'Smartes Wiederholen deaktivieren' : 'Smartes Wiederholen aktivieren';
      actionOption!.icon = this.learnset!.smartRepetitionEnabled ? 'repeat-off' : 'repeat';
      this.updateSmartRepetitionState();
    } else if (action === 'delete') {
      // this.deleteLearnset();
    }
  }

  handleFlashcardsOptionClick(selectedOption: string) {
    if (selectedOption === 'manual') {
      this.showCreateOptions = false;
      this.showManualCreate = true;
    } else if (selectedOption === 'ai') {
      this.router.navigate([`sets/${this.learnset!.id}/create/ai`]);
    }
  }

  handleFlashcardActionsClicked(flashcard: Flashcard) {
    this.currentFlashcard = flashcard;
    this.showFlashcardActions = true;
  }

  handleFlashcardAction(action: string) {
    if (action === 'edit') {
      this.showEditFlashcard = true;
    } else if (action === 'delete') {
      this.flashcardService.deleteFlashcard(this.currentFlashcard!.id!).subscribe(() => {
        const flashcard = this.learnset?.flashcards.find((flashcard) => flashcard.id !== this.currentFlashcard!.id);
        if (flashcard?.isDue()) {
          this.learnset!.openCards!--;
        }
        this.learnset!.flashcards = this.learnset!.flashcards.filter((flashcard) => flashcard.id !== this.currentFlashcard!.id);
      });
    }
    this.showFlashcardActions = false;
  }

  handleLearnOptionClick(action: string) {
    if (action === 'classic-mode') {
      this.router.navigate([`sets/${this.learnset?.id}/learn/${action}`], { queryParams: { smartRepetition: this.smartLearningSelected } });
    } else if (action === 'ai-chat') {
      this.router.navigate([`sets/${this.learnset?.id}/learn/ai-chat`]);
    } else if (action === 'text-answer-mode') {
      this.handleTextAnswerModeClicked();
    } else if (action === 'audio-mode') {
      this.handleAudioModeClicked();
    }
    this.showFlashcardActions = false;
  }

  handleTextAnswerModeClicked() {
    if (this.user && this.user.isPremiumUser) {
      this.router.navigate([`sets/${this.learnset?.id}/learn/text-answer-mode`]);
    } else {
      this.premiumFeatureDialogService.showDialog('Texteingabe-Modus', 'So funktioniert es: du wiederholst deine Karten, indem du die jeweilige Antwort eintippst. Die Sparkly-KI gibt dir direktes Feedback und stuft basierend darauf automatisch ein, wann die Lernkarte das nächste Mal wiederholt werden sollte.');
    }
  }

  handleAudioModeClicked() {
    if (this.user && this.user.isPremiumUser) {
      this.router.navigate([`sets/${this.learnset?.id}/learn/audio-mode`]);
    } else {
      this.premiumFeatureDialogService.showDialog('Lasse dich von deinem KI-Tutor abfragen', 'So funktioniert es: die KI liest dir die Vorderseite der Karte vor und du antwortest per Spracheingabe. Die Sparkly-KI gibt dir direktes Feedback und stuft basierend darauf automatisch ein, wann die Lernkarte das nächste Mal wiederholt werden sollte.');
    }
  }

  handleFlashcardsCreated() {
    this.fetchLearnset(this.learnset!.id!.toString());
    this.showManualCreate = false;
    if (this.onboarding?.learnPromptShown === false && this.learnset!.flashcards.length > 0) {
      timer(2000).subscribe(() => { // 2 Sekunden Verzögerung
        this.showLearnPromptDialog = true;
      });
    }
  }

  handleSuggestionsFinished() {
    this.showSuggestionCards = false;
    this.fetchLearnset(this.learnset!.id!.toString());
  }

  handleLearnPromptClicked() {
    this.showLearnPromptDialog = false;
    this.handleLearnOptionClick('classic-mode');
    this.saveLearnPromptShown();
  }

  saveLearnPromptShown() {
    this.onboarding!.learnPromptShown = true;
    this.onboardingService.updateOnboarding(this.onboarding!);
  }

  fetchLearnset(learnsetId: string): void {
    this.learnsetService.getLearnset(learnsetId)
      .subscribe((learnset: any) => {
        this.learnset = Learnset.constructLearnset(learnset);
        this.learnset.flashcards.sort((a, b) => a.dueDate!.getTime() - b.dueDate!.getTime());
        this.breadcrumbs = [
          {
            text: 'Kurse',
            route: 'courses'
          },
          {
            text: this.learnset.courseName,
            route: `courses/${this.learnset!.courseId}`
          },
          {
            text: this.learnset!.name,
            route: `courses/${this.learnset!.courseId}/sets/${this.learnset!.id}`
          }
        ];
        
        const actionOption = this.actionsOptions.find(option => option.action === 'toggle-smart-repetition');
        actionOption!.text = this.learnset!.smartRepetitionEnabled ? 'Smartes Wiederholen deaktivieren' : 'Smartes Wiederholen aktivieren';
        actionOption!.icon = this.learnset!.smartRepetitionEnabled ? 'repeat-off' : 'repeat';
        console.log(this.learnset);
      });
  }

  learn(smartLearningSelected: boolean) {
    this.smartLearningSelected = smartLearningSelected;
    this.showLearnOptions = true;
  }

  updateFlashcard(flashcard: Flashcard) {
    this.flashcardService.updateFlashcards([flashcard]).subscribe();
  }

  addFlashcard() {
    this.showCreateOptions = true;
  }

  handleSuggestionClick() {
    if (this.user && this.user.isPremiumUser) {
      this.showSuggestionCards = true;
    } else {
      this.premiumFeatureDialogService.showDialog('Keine Wissenslücken mehr', 'Lasse dir basierend auf deinem Lernset weitere Lernkarten vorschlagen und schließe die letzten Wissenslücken.');
    }
  }

  updateSmartRepetitionState() {
    this.learnsetService.updateLearnset(this.learnset!).subscribe();
  }

  deleteLearnset() {
    this.learnsetService.deleteLearnset(this.learnset!.id!).subscribe(result => {
      this.router.navigate([`courses/${this.learnset?.courseId}`]);
    });
  }
}
